/**
 * /admin/wiki-ops — 위키 단순화 step5 운영 화면.
 *
 * - "지금 재계산": 특정 종목 force 재계산(quarter 생략 = 서빙 최신 분기). dirty bump + 멱등 재계산.
 * - fail ledger: wiki_fail 조회. stuck(현 세대 결정적 실패로 막힘)=빨강·retryable(일시)=노랑.
 *   각 행에서 그 (symbol, quarter) 콕 집어 재계산.
 */
import { useState } from 'react';
import { api, type WikiFail } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { ErrorBox } from './Watchlist';

function fmtRelative(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const days = Math.floor(diff / 86_400_000);
  if (days >= 1) return `${days}일 전`;
  const hours = Math.floor(diff / 3_600_000);
  if (hours >= 1) return `${hours}h 전`;
  const mins = Math.floor(diff / 60_000);
  return `${mins}m 전`;
}

export default function AdminWikiOpsPage() {
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error, loading } = useAsync(() => api.wikiFails(), [reloadKey]);

  const [symbol, setSymbol] = useState('');
  const [busy, setBusy] = useState<string | null>(null); // 재계산 중인 key('symbol' 또는 'symbol/quarter')
  const [toast, setToast] = useState<string | null>(null);

  const reload = () => setReloadKey((k) => k + 1);

  const recompute = async (sym: string, quarter?: string) => {
    const key = quarter ? `${sym}/${quarter}` : sym;
    setBusy(key);
    setToast(null);
    try {
      const r = await api.recomputeWikiSymbol(sym, quarter);
      if (r.status === 'no-servable-quarter') {
        setToast(`⚠️ ${sym}: 위키·리포트가 없어 재계산할 분기 없음. 백필 먼저.`);
      } else if (r.status === 'no-input') {
        setToast(`⚠️ ${sym} ${quarter ?? ''}: 그 분기에 추출된 리포트(입력)가 없음.`);
      } else {
        const parts = r.results.map(
          (x) => `${x.quarter} ${x.committed ? '✅ 반영' : x.failed ? '❌ 실패' : `skip(${x.skipped})`}`,
        );
        setToast(`${sym} 재계산: ${parts.join(', ')}`);
      }
      reload();
    } catch (e) {
      setToast(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(symbol)) {
      setToast('symbol 은 6자리 숫자.');
      return;
    }
    recompute(symbol);
  };

  const fails = data?.fails ?? [];
  const stuckCount = fails.filter((f) => f.stuck).length;

  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-tight">🛠 위키 운영</h1>
      <p className="mt-1 text-sm text-slate-500">
        특정 종목 위키 즉시 재계산 + 실패 장부(fail ledger). 평소엔 cron 이 dirty 분기를 자동 재계산하므로 수동 개입은 드뭄.
      </p>

      {/* 지금 재계산 */}
      <form onSubmit={onSubmit} className="mt-5 flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-xs text-slate-600">
          종목코드
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="005930"
            maxLength={6}
            className="mt-0.5 w-28 rounded border border-slate-300 px-2 py-1 font-mono text-sm focus:border-indigo-500 focus:outline-none"
          />
        </label>
        <button
          type="submit"
          disabled={busy === symbol}
          className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-slate-300"
        >
          {busy === symbol ? '재계산 중…' : '지금 재계산 (서빙 분기)'}
        </button>
        <span className="text-xs text-slate-400">분기 생략 = 사용자가 보는 최신 분기 1개.</span>
      </form>

      {toast && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-sm">{toast}</div>
      )}

      {/* fail ledger */}
      <div className="mt-8 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">실패 장부 (fail ledger)</h2>
        <p className="text-sm text-slate-500">
          {fails.length}건{stuckCount > 0 && <span className="ml-1 text-red-600">· {stuckCount} stuck</span>}
        </p>
      </div>

      {error && <div className="mt-3"><ErrorBox msg={error} /></div>}
      {loading && <p className="mt-4 text-slate-500">로딩 중…</p>}

      {!loading && !error && (
        <table className="mt-3 w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2 pr-3">종목</th>
              <th className="pr-3">분기</th>
              <th className="pr-3">상태</th>
              <th className="pr-3 text-right">세대(dirty/built)</th>
              <th className="pr-3 text-right">blocked</th>
              <th className="pr-3 text-right">시도</th>
              <th className="pr-3">에러</th>
              <th className="pr-3">실패</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {fails.map((f) => (
              <FailRow key={`${f.symbol}/${f.quarter}`} f={f} busy={busy === `${f.symbol}/${f.quarter}`} onRecompute={() => recompute(f.symbol, f.quarter)} />
            ))}
            {fails.length === 0 && (
              <tr>
                <td colSpan={9} className="py-6 text-center text-slate-500">실패 기록 없음 — 깨끗함. ✨</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

function FailRow({ f, busy, onRecompute }: { f: WikiFail; busy: boolean; onRecompute: () => void }) {
  return (
    <tr className={f.stuck ? 'bg-red-50' : f.retryable ? 'bg-amber-50' : ''}>
      <td className="py-2 pr-3 font-mono text-xs">{f.symbol}</td>
      <td className="pr-3 font-mono text-xs">{f.quarter}</td>
      <td className="pr-3">
        {f.stuck ? (
          <span className="pill bg-red-100 text-red-700" title="현 세대가 결정적 실패로 막힘 — 입력 안 바뀌면 cron 이 더 안 건드림">🛑 stuck</span>
        ) : f.retryable ? (
          <span className="pill bg-amber-100 text-amber-700" title="일시 실패 — 다음 cron 이 재시도">⏳ retryable</span>
        ) : (
          <span className="pill bg-slate-100 text-slate-500" title="옛 세대 실패 — 새 입력으로 재계산되거나 이미 해소">과거</span>
        )}
      </td>
      <td className="pr-3 text-right font-mono text-xs text-slate-500">
        {f.dirtySeq ?? '—'}/{f.builtSeq ?? '—'}
      </td>
      <td className="pr-3 text-right font-mono text-xs text-slate-500">{f.blockedSeq}</td>
      <td className="pr-3 text-right font-mono text-xs text-slate-500">{f.attempt}</td>
      <td className="pr-3 max-w-xs truncate text-xs text-slate-600" title={f.error ?? ''}>{f.error ?? '—'}</td>
      <td className="pr-3 whitespace-nowrap text-xs text-slate-500">{fmtRelative(f.failedAt)}</td>
      <td>
        <button
          onClick={onRecompute}
          disabled={busy}
          className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50 disabled:opacity-40"
        >
          {busy ? '…' : '재계산'}
        </button>
      </td>
    </tr>
  );
}
